// Data Access Layer: Tracks
import { supabase } from '../supabase';
import type { Track, TrackSkillWithDetails } from '../../types/database';

/**
 * Fetch all active tracks
 */
export async function getTracks(): Promise<Track[]> {
  const { data, error } = await supabase
    .from('tracks')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) {
    console.error('Error fetching tracks:', error);
    throw new Error(`Failed to fetch tracks: ${error.message}`);
  }

  return data || [];
}

/**
 * Fetch a single track by ID
 */
export async function getTrackById(trackId: string): Promise<Track | null> {
  const { data, error } = await supabase
    .from('tracks')
    .select('*')
    .eq('id', trackId)
    .single();

  if (error) {
    console.error('Error fetching track:', error);
    throw new Error(`Failed to fetch track: ${error.message}`);
  }

  return data;
}

/**
 * Fetch a track by slug
 */
export async function getTrackBySlug(slug: string): Promise<Track | null> {
  const { data, error } = await supabase
    .from('tracks')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Error fetching track by slug:', error);
    throw new Error(`Failed to fetch track: ${error.message}`);
  }

  return data;
}

/**
 * Fetch all skills for a specific track
 */
export async function getTrackSkills(trackId: string): Promise<TrackSkillWithDetails[]> {
  const { data, error } = await supabase
    .from('track_skills')
    .select(`
      *,
      skill:skills(*)
    `)
    .eq('track_id', trackId)
    .order('order_index');

  if (error) {
    console.error('Error fetching track skills:', error);
    throw new Error(`Failed to fetch track skills: ${error.message}`);
  }

  return data || [];
}
